import { Injectable } from '@angular/core';
import { RestfulEndpoint } from '../../services/restful-endpoint.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PointImage, ProjectDetails } from '../../models/project.model';
import { FloorPointDataResponse } from '../../models/reports.model';
import { ProjectReportsResponseRefactored } from 'app/models/reports-refactored.model';
import { TimelapseReportSummary } from './models/timelapse.models';

interface ApiResponse<T> {
  status: string;
  message: string;
  error_code: string | null;
  meta_data: any;
  data: T;
}

@Injectable({
  providedIn: 'root',
})
export class DemoViewerApiService {
  constructor(private endPoint: RestfulEndpoint) {}

  /**
   * Get project details by ID
   * @param projectId The project ID
   */
  getProjectDetails(projectId: string): Observable<{ project_details: ProjectDetails }> {
    return this.endPoint.post('get_project_details', { id: projectId });
  }

  /**
   * Get point images for a specific floor and report
   * @param floorId The floor ID
   * @param reportId The report ID
   */
  getPointImagesByFloorId(
    floorId: string,
    reportId: string,
  ): Observable<ApiResponse<PointImage[]>> {
    return this.endPoint.get(`floors/${floorId}/images`, null, `reports/${reportId}`);
  }

  /**
   * Get video full path for a report
   * @param floorId The floor ID
   * @param reportId The report ID
   */
  getVideoPoints(floorId: string, reportId: string): Observable<ApiResponse<PointImage[]>> {
    return this.endPoint.get(
      `${reportId}/points-extracted-video?floor_id=${floorId}`,
      null,
      'reports',
    );
  }

  /**
   * Get default floor data for a report
   * @param reportId The report ID
   */
  getReportDefaultFloor(reportId: string): Observable<FloorPointDataResponse> {
    return this.endPoint.get(`reports/${reportId}/get-default-point-data`);
  }

  /**
   * Get project reports by project ID
   * @param projectId The project ID
   * @param perPage Number of items per page
   * @param page Page number
   * @param reportType Optional report type filter
   */
  getProjectReports(
    projectId: string,
    perPage: number = 10,
    page: number = 1,
    reportType?: string,
  ): Observable<any> {
    const params = new URLSearchParams();
    params.append('per_page', perPage.toString());
    params.append('page', page.toString());
    if (reportType) {
      params.append('report_type', reportType);
    }

    return this.endPoint.get(`projects/${projectId}/reports?${params.toString()}`);
  }

  /**
   * Set default point image direction
   * @param reportId The report ID
   * @param pointImageId The point image ID
   * @param horizontalAngle The horizontal angle
   * @param verticalAngle The vertical angle
   */
  setDefaultPointImageDirection(
    reportId: string,
    pointImageId: string,
    horizontalAngle: number,
    verticalAngle: number,
  ): Observable<any> {
    const body = {
      report_id: reportId,
      point_image_id: pointImageId,
      horizontal_angle: horizontalAngle,
      vertical_angle: verticalAngle,
    };

    return this.endPoint.post('set_default_point_image_direction', body);
  }

  /**
   * Get navigation hotspots for a floor
   * @param reportId The report ID
   * @param floorId The floor ID
   */
  getNavigationHotspots(reportId: string, floorId: string): Observable<any> {
    return this.endPoint.get(`floors/${floorId}/navigation-hotspots`, null, `reports/${reportId}`);
  }

  /**
   * Retrieve point images for a specific point across multiple reports (timelapse use-case)
   */
  getPointImagesByPointAndReports(
    pointId: string,
    reportIds: string[],
  ): Observable<ApiResponse<PointImage[]>> {
    const params = new URLSearchParams();
    reportIds?.forEach((id) => {
      if (id) {
        params.append('report_ids[]', id);
      }
    });

    const query = params.toString();
    const endpoint = query
      ? `floors/point/${pointId}/images?${query}`
      : `floors/point/${pointId}/images`;

    return this.endPoint.get(endpoint);
  }

  /**
   * Trigger or poll timelapse video generation for a point
   */
  generateTimelapseVideo(
    pointId: string,
    reportIds: string[],
    requested: boolean,
  ): Observable<{ timelapse_zip_file: string; waiting: boolean }> {
    return this.endPoint
      .post('v2/reports/generate-timelapse-video', {
        report_ids: reportIds,
        point_id: pointId,
        requested,
      })
      .pipe(map((response: any) => response?.data ?? response));
  }

  /**
   * Get matching points for a floor
   * @param reportId The report ID
   * @param floorId The floor ID
   */
  getMatchingPoints(reportId: string, floorId: string): Observable<any> {
    return this.endPoint.get(`floors/${floorId}/path-matching-points`, null, `reports/${reportId}`);
  }

  /**
   * Get walkthroughs for a floor
   * @param reportId The report ID
   * @param floorId The floor ID
   */
  getWalkthroughs(reportId: string, floorId: string): Observable<any> {
    return this.endPoint.get(`floors/${floorId}/navigation-hotspots`, null, `reports/${reportId}`);
  }

  getProjectReportsRefactoredByReportId(
    projectId: string,
    report_id: string,
  ): Observable<ProjectReportsResponseRefactored> {
    const params = new URLSearchParams();
    params.append('report_id', report_id.toString());

    return this.endPoint.get(`projects/${projectId}/reports?${params.toString()}`);
  }

  getReportsHavingPointImages(pointId: string): Observable<ApiResponse<TimelapseReportSummary[]>> {
    return this.endPoint.get(`floors/point/${pointId}/reports-having-point-images`);
  }
}
